import { useQuery, UseQueryResult } from '@tanstack/react-query';

import {
  isCateringOrderFlow,
  isYelpPlatform,
  getTrafficSource,
} from '@local/utils';
import { getApiHost, getQueryFetchOptions, parseHttpResponse } from '../utils';
import { CountryCode, Currency, isCountryCode } from '../types';
import { ApiRestaurant, Restaurant } from './restaurant';

const BASE_QUERY_KEY = 'restaurant';

export const useRestaurantQuery: (
  restaurantId: string,
  enabled?: boolean
) => UseQueryResult<Restaurant, Error> = (restaurantId, enabled = true) => {
  const endpoint = getEndpoint(restaurantId);
  const searchParams = getSearchParams(restaurantId);

  return useQuery<Restaurant>({
    enabled: enabled && !!restaurantId,
    queryKey: [BASE_QUERY_KEY, restaurantId],
    queryFn: async () => {
      const apiHost = getApiHost();
      const url = new URL(`/api/${endpoint}`, apiHost);
      url.search = searchParams?.toString() ?? '';

      const response = await fetch(url, getQueryFetchOptions());
      const apiRestaurant = await parseHttpResponse<ApiRestaurant>(response);
      return {
        ...apiRestaurant,
        country_code: getCountryCode(apiRestaurant),
        currency: getCurrency(apiRestaurant),
      };
    },
  });
};

const getEndpoint = (id: string): string => {
  if (isYelpPlatform()) {
    return `yelp/${id}/restaurant`;
  } else if (isCateringOrderFlow()) {
    return `restaurant/${id}/catering`;
  } else {
    return `restaurant/${id}`;
  }
};

const getSearchParams = (restaurantId: string): URLSearchParams | null => {
  const trafficSource = isYelpPlatform()
    ? 'yelp'
    : getTrafficSource(restaurantId);
  return trafficSource
    ? new URLSearchParams({ cn_channel: trafficSource })
    : null;
};

export const getCountryCode = (
  restaurant: Pick<ApiRestaurant, 'address'>
): CountryCode => {
  const countryCode = restaurant?.address?.country_alpha2;
  return isCountryCode(countryCode) ? countryCode : 'US';
};

export const getCurrency = (
  restaurant: Pick<ApiRestaurant, 'address'>
): Currency => {
  return getCountryCode(restaurant) === 'CA' ? 'CAD' : 'USD';
};
